import { useQuery } from '@apollo/client';
import { faEllipsisVertical, faRobot, faBarsFilter } from '@fortawesome/pro-regular-svg-icons';
import { faCircle } from '@fortawesome/pro-solid-svg-icons';
import * as React from 'react';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import Button from '../../../core/components/Button.tsx';
import ConfirmModal from '../../../core/components/ConfirmModal.tsx';
import DropdownMenu from '../../../core/components/DropdownMenu.tsx';
import EmptyStage from '../../../core/components/EmptyStage.tsx';
import Filter from '../../../core/components/Filter.tsx';
import { withIcon } from '../../../core/components/Icon.tsx';
import { GET_NAVIGATION_CHANNELS } from '../../../core/components/Navigation/internal/queries.ts';
import Page from '../../../core/components/Page';
import SearchField from '../../../core/components/SearchField.tsx';
import Table, { TableScrollWrapper } from '../../../core/components/Table.tsx';
import TablePagination from '../../../core/components/TablePagination.tsx';
import { SAD } from '../../../core/components/worki/Pose.tsx';
import classnames from '../../../core/utils/classnames.tsx';
import { url } from '../../../core/utils/link.ts';
import useSearchParamState, {
    useArraySearchParamState,
    useBooleanSearchParamState,
} from '../../../core/utils/useSearchParamState.tsx';
import CreateCustomCategoryModal, {
    CategoryFormData,
    CATEGORY_FORM_DEFAULT_VALUES,
    OUTLOOK_CATEGORY_COLORS,
} from '../../../customer/pages/SmartInbox/CreateCustomCategoryModal.tsx';
import { darkenHex, getCategoryOutlookTag } from '../../../customer/pages/SmartInbox/customCategoryMapping.ts';
import { RecommendedCategory } from '../../../customer/pages/SmartInbox/RecommendedCategoriesGrid.tsx';
import { SmartInboxCustomCategoryType, SmartInboxIntegrationType } from '../../../customer/pages/SmartInbox/types.ts';
import { MAILBOX_CATEGORY_DETAIL_PATH } from '../constants.ts';
import useCategoryCreate from '../hooks/useCategoryCreate.ts';
import useCategoryOperations from '../hooks/useCategoryOperations.ts';

const EllipsisIcon = withIcon(faEllipsisVertical);
const AgentIcon = withIcon(faRobot);
const FilterListIcon = withIcon(faBarsFilter);
const DotIcon = withIcon(faCircle);

const DEFAULT_PAGE_SIZE = 10;

// sentinel value for filtering categories with no connected agent, distinct from any real agent name
const NO_AGENT_VALUE = '__no_agent__';

interface CategoriesTabProps {
    integration: SmartInboxIntegrationType;
    connectedAgentName: string | null;
    refetch: () => Promise<any>;
    tabsNav: React.ReactNode;
}

export interface CategoriesTabHandle {
    openCreateModal: () => void;
}

const CategoriesTab = React.forwardRef<CategoriesTabHandle, CategoriesTabProps>(function CategoriesTab(
    { integration, connectedAgentName, refetch, tabsNav },
    ref
) {
    const { t } = useTranslation('mailboxes');
    const navigate = useNavigate();

    const [search, setSearch] = useSearchParamState('categorySearch', '');
    const [activePage, setActivePage] = useSearchParamState('categoryPage', '1');
    const [itemsPerPage, setItemsPerPage] = useSearchParamState('categoryPageSize', DEFAULT_PAGE_SIZE.toString());
    const [showFilters, setShowFilters] = useBooleanSearchParamState('categoryFiltersOpen', false);
    const [agentFilter, setAgentFilter] = useArraySearchParamState('categoryAgentFilter');
    const [openMenuId, setOpenMenuId] = useState<string | null>(null);
    const [modalOpen, setModalOpen] = useState(false);

    const { data: channelData } = useQuery(GET_NAVIGATION_CHANNELS, { fetchPolicy: 'network-only' });
    const { createCustomCategory, addRecommendedCategories, creating, addingRecommended } = useCategoryCreate(
        integration.id,
        channelData
    );
    const form = useForm<CategoryFormData>({ defaultValues: CATEGORY_FORM_DEFAULT_VALUES, mode: 'onChange' });

    const { categoryToDelete, setCategoryToDelete, pendingActionId, deleting, handleTogglePause, handleDelete } =
        useCategoryOperations<SmartInboxCustomCategoryType>(refetch);

    const categories = integration.customCategories || [];
    const hasCategories = categories.length > 0;
    const hasActiveFilters = agentFilter.length > 0;

    const agentOptions = useMemo(() => {
        const options = connectedAgentName ? [{ value: connectedAgentName, label: connectedAgentName }] : [];
        return [
            ...options,
            {
                value: NO_AGENT_VALUE,
                label: t('detail.categoriesTab.filters.agent.noAgent'),
                separatorBefore: options.length > 0,
            },
        ];
    }, [connectedAgentName, t]);

    const filtered = categories.filter((c) => {
        if (!c.name.toLowerCase().includes(search.toLowerCase())) return false;
        if (agentFilter.length) {
            const hasAgent = c.sourceType === 'AGENT' && !!connectedAgentName;
            const matchesAgent = hasAgent && connectedAgentName && agentFilter.includes(connectedAgentName);
            const matchesNoAgent = !hasAgent && agentFilter.includes(NO_AGENT_VALUE);
            if (!matchesAgent && !matchesNoAgent) return false;
        }
        return true;
    });
    const parsedPage = parseInt(activePage || '1', 10);
    const parsedPageSize = parseInt(itemsPerPage || DEFAULT_PAGE_SIZE.toString(), 10);
    const visibleCategories = filtered.slice((parsedPage - 1) * parsedPageSize, parsedPage * parsedPageSize);

    const goToCategoryDetail = (category: SmartInboxCustomCategoryType) =>
        navigate(url(MAILBOX_CATEGORY_DETAIL_PATH, { id: integration.id, categoryId: category.id }));

    const openCreateModal = () => {
        form.reset(CATEGORY_FORM_DEFAULT_VALUES);
        setModalOpen(true);
    };

    const clearFilters = () => {
        setSearch('');
        setAgentFilter([]);
        setActivePage('1');
    };

    React.useImperativeHandle(ref, () => ({ openCreateModal }));

    const openCreatedCategory = async (createdIds: string[] | null) => {
        if (!createdIds) return;
        setModalOpen(false);
        if (createdIds.length === 1) {
            navigate(url(MAILBOX_CATEGORY_DETAIL_PATH, { id: integration.id, categoryId: createdIds[0] }));
            return;
        }
        await refetch();
    };

    const handleSubmit = async (formData: CategoryFormData) =>
        openCreatedCategory(await createCustomCategory(formData));

    const handleCreateRecommended = async (recommended: RecommendedCategory[]) =>
        openCreatedCategory(await addRecommendedCategories(recommended));

    return (
        <>
            <Page.Header className="py-0 justify-between" primary={false}>
                <div className="flex h-full">{tabsNav}</div>
                {hasCategories && (
                    <div className="flex items-center gap-2 py-2.5">
                        <SearchField
                            placeholder={t('detail.categoriesTab.searchPlaceholder')}
                            value={search}
                            onValueChange={(value) => {
                                setSearch(value);
                                setActivePage('1');
                            }}
                        />
                        <button
                            type="button"
                            className={classnames(
                                'flex items-center gap-2 h-8 px-3 rounded cursor-pointer text-sm font-medium text-content-strong hover:bg-surface-strong',
                                (showFilters || hasActiveFilters) &&
                                    '!bg-surface-brand-medium-default !text-content-brand-strong'
                            )}
                            onClick={() => setShowFilters(!showFilters)}
                        >
                            <FilterListIcon />
                            {t('detail.categoriesTab.filters.toggleLabel')}
                            {hasActiveFilters && <span>({agentFilter.length})</span>}
                        </button>
                    </div>
                )}
            </Page.Header>

            {showFilters && hasCategories && (
                <div className="flex items-center gap-3 px-7 py-2 border-b border-solid border-line-weak bg-surface-medium flex-none">
                    <Filter
                        label={t('detail.categoriesTab.filters.agent.label')}
                        allLabel={t('detail.categoriesTab.filters.agent.all')}
                        options={agentOptions}
                        selected={agentFilter}
                        onSelectedChange={(value) => {
                            setAgentFilter(value);
                            setActivePage('1');
                        }}
                        multiple
                    />
                </div>
            )}

            <Page.Content lowered className="flex flex-col gap-4">
                {!hasCategories ? (
                    <EmptyStage
                        size={88}
                        title={t('detail.categoriesTab.emptyTitle')}
                        description={t('detail.categoriesTab.emptyDescription')}
                        action={
                            <Button variant="primary" onClick={openCreateModal}>
                                {t('detail.categoriesTab.addCategory')}
                            </Button>
                        }
                    />
                ) : visibleCategories.length === 0 ? (
                    <EmptyStage
                        size={88}
                        pose={SAD}
                        title={t('detail.categoriesTab.noResultsTitle')}
                        description={t('detail.categoriesTab.noResultsDescription')}
                        action={
                            <Button variant="outline" onClick={clearFilters}>
                                {t('detail.categoriesTab.clearFilters')}
                            </Button>
                        }
                    />
                ) : (
                    <TableScrollWrapper>
                        <Table className="w-full table-auto">
                            <Table.Head sticky>
                                <Table.Row>
                                    <Table.HeadCell>{t('detail.categoriesTab.columns.category')}</Table.HeadCell>
                                    <Table.HeadCell>{t('detail.categoriesTab.columns.agent')}</Table.HeadCell>
                                    <Table.HeadCell>{t('detail.categoriesTab.columns.outlookTag')}</Table.HeadCell>
                                    <Table.HeadCell>{t('detail.categoriesTab.columns.status')}</Table.HeadCell>
                                    <Table.HeadCell sticky="right" />
                                </Table.Row>
                            </Table.Head>
                            <Table.Body>
                                {visibleCategories.map((category) => {
                                    const outlookTag = getCategoryOutlookTag(category);
                                    const tagHex = outlookTag?.colorName
                                        ? OUTLOOK_CATEGORY_COLORS.find((c) => c.name === outlookTag.colorName)?.hex
                                        : undefined;

                                    return (
                                        <Table.Row
                                            key={category.id}
                                            className="cursor-pointer hover:bg-surface-weak transition-colors group [&>td]:group-hover:bg-surface-strong"
                                            onClick={() => goToCategoryDetail(category)}
                                        >
                                            <Table.Cell>
                                                <span className="font-medium text-content-strong">{category.name}</span>
                                            </Table.Cell>
                                            <Table.Cell>
                                                {category.sourceType === 'AGENT' && connectedAgentName ? (
                                                    <span className="flex items-center gap-1 text-xs font-medium px-1.5 py-0.5 rounded bg-surface-agent-weak text-content-agent-default flex-none w-fit">
                                                        <AgentIcon className="text-[10px]" />
                                                        {connectedAgentName}
                                                    </span>
                                                ) : (
                                                    <span className="text-content-weak">
                                                        {t('detail.categoriesTab.automationNotAdded')}
                                                    </span>
                                                )}
                                            </Table.Cell>
                                            <Table.Cell>
                                                {outlookTag ? (
                                                    <span
                                                        className="text-xs font-medium px-1.5 py-0.5 rounded text-content-strong"
                                                        style={{
                                                            backgroundColor: tagHex,
                                                            color: tagHex ? darkenHex(tagHex) : undefined,
                                                        }}
                                                    >
                                                        {outlookTag.name}
                                                    </span>
                                                ) : (
                                                    <span className="text-content-weak">
                                                        {t('detail.categoriesTab.automationNotAdded')}
                                                    </span>
                                                )}
                                            </Table.Cell>
                                            <Table.Cell className="whitespace-nowrap">
                                                <span
                                                    className={classnames(
                                                        'text-content-strong font-medium text-sm items-center border-[1px] px-2 py-0.5 rounded',
                                                        category.enabled
                                                            ? 'text-content-brand-medium border-line-success-weak bg-surface-success-weak'
                                                            : 'text-content-warning-default border-line-warning-weak bg-surface-warning-weak'
                                                    )}
                                                >
                                                    <DotIcon
                                                        className={classnames(
                                                            'text-[10px] pr-2 pb-[1px]',
                                                            category.enabled
                                                                ? 'text-content-success-weak'
                                                                : 'text-content-warning-weak'
                                                        )}
                                                    />
                                                    {category.enabled
                                                        ? t('detail.categoriesTab.statusActive')
                                                        : t('detail.categoriesTab.statusPaused')}
                                                </span>
                                            </Table.Cell>
                                            <Table.Cell sticky="right" onClick={(e) => e.stopPropagation()}>
                                                <div className="flex justify-end">
                                                    <DropdownMenu
                                                        open={openMenuId === category.id}
                                                        onOpenChange={(open) =>
                                                            setOpenMenuId(open ? category.id : null)
                                                        }
                                                    >
                                                        <DropdownMenu.Trigger asChild>
                                                            <button
                                                                className="flex items-center justify-center w-8 h-8 rounded hover:bg-surface-strong-hover cursor-pointer disabled:opacity-50 disabled:pointer-events-none"
                                                                disabled={pendingActionId === category.id}
                                                            >
                                                                <EllipsisIcon />
                                                            </button>
                                                        </DropdownMenu.Trigger>
                                                        <DropdownMenu.Content align="end">
                                                            <DropdownMenu.Item
                                                                onClick={() => handleTogglePause(category)}
                                                            >
                                                                {category.enabled
                                                                    ? t('detail.categoriesTab.actionPause')
                                                                    : t('detail.categoriesTab.actionResume')}
                                                            </DropdownMenu.Item>
                                                            <DropdownMenu.Item
                                                                onClick={() => goToCategoryDetail(category)}
                                                            >
                                                                {t('detail.categoriesTab.actionEdit')}
                                                            </DropdownMenu.Item>
                                                            <DropdownMenu.Separator />
                                                            <DropdownMenu.Item
                                                                danger
                                                                onClick={() => setCategoryToDelete(category)}
                                                            >
                                                                {t('detail.categoriesTab.actionDelete')}
                                                            </DropdownMenu.Item>
                                                        </DropdownMenu.Content>
                                                    </DropdownMenu>
                                                </div>
                                            </Table.Cell>
                                        </Table.Row>
                                    );
                                })}
                            </Table.Body>
                        </Table>
                    </TableScrollWrapper>
                )}

                {filtered.length > 0 && (
                    <TablePagination
                        totalCount={filtered.length}
                        activePage={parsedPage}
                        itemsPerPage={parsedPageSize}
                        setActivePage={(page) => setActivePage(page.toString())}
                        setItemsPerPage={(size) => setItemsPerPage(size.toString())}
                    />
                )}

                {modalOpen && (
                    <CreateCustomCategoryModal
                        form={form}
                        destinationFolders={integration.destinationFolders || []}
                        isLoading={creating || addingRecommended}
                        integrationId={integration.id}
                        channels={channelData}
                        onClose={() => setModalOpen(false)}
                        onSubmit={handleSubmit}
                        onCreateRecommended={handleCreateRecommended}
                        title={t('detail.categoriesTab.createTitle')}
                        submitButtonLabel={t('detail.categoriesTab.createSubmit')}
                        quickCreate
                    />
                )}

                {categoryToDelete && (
                    <ConfirmModal
                        onConfirm={handleDelete}
                        onCancel={() => setCategoryToDelete(null)}
                        title={t('detail.categoriesTab.deleteConfirmTitle', { name: categoryToDelete.name })}
                        labelConfirm={t('detail.categoriesTab.actionDelete')}
                        confirmDisabled={deleting}
                        danger
                    >
                        {t('detail.categoriesTab.deleteConfirmMessage', { name: categoryToDelete.name })}
                    </ConfirmModal>
                )}
            </Page.Content>
        </>
    );
});

export default CategoriesTab;
